import { useEffect, useId, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { getUrl, uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { useCompanyContext } from '../../../contexts/CompanyContext';
import { useUserContext } from '../../../contexts/UserContext';
import { S3_IMAGE_PATH } from '../../../utils/constants';
import { deleteImageFromS3, resizeLogoFile, resizeLogoPngFile } from '../../../utils/helperFunctions';
import { Btn } from '../../ui';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ACCEPTED_LOGO_TYPES = new Set(['image/jpeg', 'image/png', 'image/jpg']);

const isPublicStoragePath = (path: string) => path.split('/')[0] === 'public';

const isPngFile = (file: File) =>
    (file.type || '').toLowerCase() === 'image/png' || /\.png$/i.test(file.name);

const prepareLogoFile = async (file: File): Promise<File> => {
    // Resize PNGs preserving transparency, JPEGs converted to compressed JPEG.
    if (isPngFile(file)) {
        return resizeLogoPngFile(file);
    }
    return resizeLogoFile(file);
};

export const CompanyInfoSection = () => {
    const { company, companyOwner } = useUserContext();
    const { updateCompanyInDatabase } = useCompanyContext();
    const [logoUrl, setLogoUrl] = useState('');
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [logoError, setLogoError] = useState<string | null>(null);
    const logoInputRef = useRef<HTMLInputElement>(null);
    const logoInputId = useId();
    const logoLabelId = useId();
    const logoHintId = useId();
    const logoStatusId = useId();
    const sectionTitleId = useId();

    const companyName = company?.name || '';
    const storedLogoPath = company?.logo || '';
    const businessType = company?.businessType || '';
    const primaryLocation = company?.primaryLocation || '';
    const billingContactAddress = company?.billingContactAddress || '';
    const billingContactName = company?.billingContactName || company?.primaryContactName || '';
    const billingContactEmail = company?.billingContactEmail || company?.primaryContactEmail || '';
    const ownerId = company?.companyOwnerId || companyOwner?.id || '';

    const initials = companyName
        ? companyName
              .split(' ')
              .map((w: string) => w[0])
              .join('')
              .toUpperCase()
              .slice(0, 2)
        : '?';

    useEffect(() => {
        let isCancelled = false;

        if (!storedLogoPath) {
            setLogoUrl('');
            return;
        }

        (async () => {
            if (!isPublicStoragePath(storedLogoPath)) {
                if (!isCancelled) {
                    setLogoUrl(storedLogoPath);
                }
                return;
            }

            try {
                const result = await getUrl({ path: storedLogoPath });
                if (!isCancelled) {
                    setLogoUrl(result.url.href);
                }
            } catch (error) {
                console.error('Error loading company logo:', error);
                if (!isCancelled) {
                    setLogoUrl('');
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [storedLogoPath]);

    const openLogoPicker = () => {
        if (isUploadingLogo) return;
        setLogoError(null);
        logoInputRef.current?.click();
    };

    const handleLogoSelect = async (file: File) => {
        if (!company?.id) {
            setLogoError('Company is not set up yet, so logos cannot be saved.');
            return;
        }
        if (!ownerId) {
            setLogoError('Company owner is not set up yet, so logos cannot be saved.');
            return;
        }

        const type = (file.type || '').toLowerCase();
        const looksLikeAccepted = ACCEPTED_LOGO_TYPES.has(type) || /\.(jpe?g|png)$/i.test(file.name);
        if (!looksLikeAccepted) {
            setLogoError('Please choose a JPG or PNG image.');
            return;
        }
        if (file.size > MAX_LOGO_BYTES) {
            setLogoError('Logo must be 2MB or smaller.');
            return;
        }

        setIsUploadingLogo(true);
        setLogoError(null);

        try {
            const imageFile = await prepareLogoFile(file);
            const safeName = (companyName || 'company').replace(/\s+/g, '-');
            const fileName = `${uuidv4()}-LOGO-${safeName}-${imageFile.name}`;
            const savedImage = await uploadData({
                path: S3_IMAGE_PATH.companyLogoImagePath(ownerId, fileName),
                data: imageFile,
            }).result;

            await updateCompanyInDatabase(company.id, { logo: savedImage.path });

            if (storedLogoPath && storedLogoPath !== savedImage.path && isPublicStoragePath(storedLogoPath)) {
                try {
                    await deleteImageFromS3(storedLogoPath);
                } catch {
                    // Non-blocking: old object may already be gone.
                }
            }
        } catch (error) {
            console.error('Failed to upload company logo:', error);
            Sentry.captureException(error);
            setLogoError('Could not upload the company logo. Please try again.');
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const handleLogoRemove = async () => {
        if (!company?.id || !storedLogoPath || isUploadingLogo) return;

        setIsUploadingLogo(true);
        setLogoError(null);
        setLogoUrl('');

        try {
            await updateCompanyInDatabase(company.id, { logo: '' });

            if (isPublicStoragePath(storedLogoPath)) {
                try {
                    await deleteImageFromS3(storedLogoPath);
                } catch {
                    // Non-blocking cleanup.
                }
            }
        } catch (error) {
            console.error('Failed to remove company logo:', error);
            Sentry.captureException(error);
            setLogoError('Could not remove the company logo. Please try again.');
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const logoUploadLabel = logoUrl ? 'Change company logo' : 'Upload company logo';
    const logoDescribedBy = [
        logoHintId,
        isUploadingLogo ? logoStatusId : null,
        logoError ? `${logoStatusId}-error` : null,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <section className="settings-section" aria-labelledby={sectionTitleId}>
            <div className="settings-section-header">
                <div>
                    <h2 id={sectionTitleId} className="settings-section-title">
                        Company Information
                    </h2>
                    <p className="settings-section-subtitle">Displayed across the app for all users in your account</p>
                </div>
            </div>

            <div className="settings-section-body">
                {/* Company Logo */}
                <div className="profile-photo-row">
                    {logoUrl ? (
                        <img
                            src={logoUrl}
                            alt={companyName ? `${companyName} logo` : 'Company logo'}
                            className="company-logo-preview"
                            onError={() => setLogoUrl('')}
                        />
                    ) : (
                        <div className="company-logo-placeholder" aria-hidden="true">
                            {initials}
                        </div>
                    )}
                    <div className="profile-photo-info">
                        <label id={logoLabelId} htmlFor={logoInputId} className="profile-photo-label">
                            Company Logo
                        </label>
                        <span id={logoHintId} className="profile-photo-hint">
                            Appears in the sidebar and on PDF reports. PNG with transparent background recommended. Max
                            2MB.
                        </span>
                        <div className="profile-photo-actions">
                            <Btn
                                variant="primary"
                                buttonSize="sm"
                                buttonText={isUploadingLogo ? 'Uploading…' : logoUrl ? 'Change' : 'Upload'}
                                buttonDisabled={isUploadingLogo}
                                buttonOnClick={openLogoPicker}
                                ariaLabel={logoUploadLabel}
                                buttonAriaProps={{
                                    'aria-controls': logoInputId,
                                    'aria-describedby': logoDescribedBy,
                                }}
                            />
                            <Btn
                                variant="secondary-outlined"
                                buttonSize="sm"
                                buttonText="Remove"
                                buttonDisabled={isUploadingLogo || !storedLogoPath}
                                buttonOnClick={handleLogoRemove}
                                ariaLabel="Remove company logo"
                            />
                        </div>
                        {isUploadingLogo && (
                            <span id={logoStatusId} className="profile-photo-status" role="status" aria-live="polite">
                                Uploading company logo…
                            </span>
                        )}
                        {logoError && (
                            <p
                                id={`${logoStatusId}-error`}
                                className="settings-form-error profile-photo-error"
                                role="alert"
                            >
                                {logoError}
                            </p>
                        )}
                    </div>
                    <input
                        ref={logoInputRef}
                        id={logoInputId}
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png"
                        className="profile-photo-input"
                        disabled={isUploadingLogo}
                        aria-labelledby={logoLabelId}
                        aria-describedby={logoDescribedBy}
                        tabIndex={-1}
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) {
                                void handleLogoSelect(file);
                            }
                            event.target.value = '';
                        }}
                    />
                </div>

                {/* Fields */}
                <div className="settings-field-grid">
                    <div className="settings-field settings-field--full">
                        <label className="settings-field-label" htmlFor="settings-company-name">
                            Company Name
                        </label>
                        <input
                            id="settings-company-name"
                            type="text"
                            className="settings-field-input"
                            value={companyName}
                            readOnly
                            disabled
                        />
                    </div>
                    <div className="settings-field">
                        <label className="settings-field-label" htmlFor="settings-industry">
                            Industry
                        </label>
                        <input
                            id="settings-industry"
                            type="text"
                            className="settings-field-input"
                            value={businessType}
                            readOnly
                            disabled
                        />
                    </div>
                    <div className="settings-field">
                        <label className="settings-field-label" htmlFor="settings-primary-location">
                            Primary Location
                        </label>
                        <input
                            id="settings-primary-location"
                            type="text"
                            className="settings-field-input"
                            value={primaryLocation}
                            readOnly
                            disabled
                        />
                    </div>
                    <div className="settings-field settings-field--full">
                        <label className="settings-field-label" htmlFor="settings-street-address">
                            Street Address
                        </label>
                        <input
                            id="settings-street-address"
                            type="text"
                            className="settings-field-input"
                            value={billingContactAddress}
                            readOnly
                            disabled
                            placeholder="e.g. 1452 Industrial Pkwy"
                        />
                    </div>
                    <div className="settings-field">
                        <label className="settings-field-label" htmlFor="settings-billing-contact-name">
                            Billing Contact Name
                        </label>
                        <input
                            id="settings-billing-contact-name"
                            type="text"
                            className="settings-field-input"
                            value={billingContactName}
                            readOnly
                            disabled
                        />
                    </div>
                    <div className="settings-field">
                        <label className="settings-field-label" htmlFor="settings-billing-email">
                            Billing Email
                        </label>
                        <input
                            id="settings-billing-email"
                            type="email"
                            className="settings-field-input"
                            value={billingContactEmail}
                            readOnly
                            disabled
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};
